import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "../ThemeContext";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { CHARTS } from "../data/content";

// ─── NAV ───────────────────────────────────────────────
export function Nav() {
  const { dark, toggle } = useTheme();
  const [scroll, setScroll] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      const pct = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      setScroll(Math.min(pct, 100));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: "Home" },
    { to: "/blog", label: "Blog" },
    { to: "/guides", label: "Guides" },
  ];

  return (
    <>
      <div className="scroll-progress" style={{ width: `${scroll}%` }} />
      <nav className="nav">
        <Link to="/" className="nav-logo">sj<span>_downunder</span></Link>
        <div className="nav-center">
          {links.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`nav-link${location.pathname === to ? " active" : ""}`}
            >
              {label}
            </Link>
          ))}
        </div>
        <div className="nav-right">
          <button className="theme-toggle" onClick={toggle} aria-label="Toggle theme">
            <div className={`theme-toggle-thumb ${dark ? "thumb-right" : "thumb-left"}`}>
              {dark ? "🌙" : "☀️"}
            </div>
          </button>
        </div>
      </nav>
    </>
  );
}

// ─── FOOTER ────────────────────────────────────────────
export function Footer() {
  const socials = [
    {
      label: "YouTube",
      href: "https://youtube.com/@sj_downunder",
      icon: (
        <svg width="18" height="13" viewBox="0 0 20 14" fill="#FF0000">
          <path d="M19.6 2.2a2.5 2.5 0 0 0-1.75-1.77C16.25 0 10 0 10 0S3.75 0 2.15.43A2.5 2.5 0 0 0 .4 2.2 26.2 26.2 0 0 0 0 7a26.2 26.2 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.75 1.77C3.75 14 10 14 10 14s6.25 0 7.85-.43a2.5 2.5 0 0 0 1.75-1.77A26.2 26.2 0 0 0 20 7a26.2 26.2 0 0 0-.4-4.8z" />
          <polygon fill="white" points="8,10 13,7 8,4" />
        </svg>
      ),
    },
    {
      label: "Instagram",
      href: "https://www.instagram.com/sj_downunder",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="url(#ig)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <defs>
            <linearGradient id="ig" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#f09433" />
              <stop offset="25%" stopColor="#e6683c" />
              <stop offset="50%" stopColor="#dc2743" />
              <stop offset="75%" stopColor="#cc2366" />
              <stop offset="100%" stopColor="#bc1888" />
            </linearGradient>
          </defs>
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="1" fill="url(#ig)" stroke="none" />
        </svg>
      ),
    },
    {
      label: "TikTok",
      href: "https://www.tiktok.com/@sj_downunder",
      icon: (
        <svg width="16" height="18" viewBox="0 0 24 24" fill="white">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.32 6.32 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z" />
        </svg>
      ),
    },
    {
      label: "LinkedIn",
      href: "https://www.linkedin.com/in/saketj32",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
    },
  ];

  return (
    <footer>
      <div className="footer-logo">sj<span>_downunder</span></div>
      <div className="footer-socials">
        {socials.map(({ label, href, icon }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="footer-social-link"
            aria-label={label}
            title={label}
          >
            {icon}
          </a>
        ))}
      </div>
      <ul className="footer-links">
        {[["Home", "/"], ["Blog", "/blog"], ["Guides", "/guides"], ["YouTube", "https://youtube.com/@sj_downunder"]].map(([label, href]) => (
          <li key={label}>
            {href.startsWith("http")
              ? <a href={href} target="_blank" rel="noopener noreferrer">{label}</a>
              : <Link to={href}>{label}</Link>}
          </li>
        ))}
      </ul>
      <div className="footer-copy">© {new Date().getFullYear()} sj_downunder</div>
    </footer>
  );
}

// ─── NEWSLETTER ────────────────────────────────────────
const FORM_NAME = "sj-newsletter";

export function Newsletter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const encode = (data) =>
    Object.keys(data).map(k => encodeURIComponent(k) + "=" + encodeURIComponent(data[k])).join("&");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": FORM_NAME, email }),
      });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="newsletter-section" id="newsletter">
      <form name={FORM_NAME} data-netlify="true" hidden>
        <input type="email" name="email" />
      </form>
      <div className="nl-left">
        <div className="nl-label">Stay in the loop</div>
        <h2 className="nl-title">New story?<br />First to know.</h2>
      </div>
      <div className="nl-right">
        {status === "success" ? (
          <p style={{ color: "white", fontFamily: "'Playfair Display',serif", fontStyle: "italic", fontSize: "1.1rem" }}>
            ✓ You're in! Welcome to the crew.
          </p>
        ) : (
          <>
            <form className="nl-form" name={FORM_NAME} method="POST" data-netlify="true" onSubmit={handleSubmit}>
              <input type="hidden" name="form-name" value={FORM_NAME} />
              <input className="nl-input" type="email" name="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
              <button type="submit" className="nl-btn" disabled={status === "submitting"}>
                {status === "submitting" ? "..." : "Subscribe"}
              </button>
            </form>
            {status === "error" && <p style={{ color: "#ffaaaa", fontSize: "0.8rem" }}>Something went wrong - try again.</p>}
            <p className="nl-note">No spam. Honest stories from down under - when I have something worth saying.</p>
          </>
        )}
      </div>
    </section>
  );
}

// ─── MARQUEE ───────────────────────────────────────────
export function Marquee({ items }) {
  const doubled = [...items, ...items];
  return (
    <div className="marquee-section">
      <div className="marquee-track">
        {doubled.map((item, i) => (
          <span key={i} className={`marquee-item${item.accent ? " accent" : ""}`}>{item.text}</span>
        ))}
      </div>
    </div>
  );
}

// ─── YOUTUBE EMBED ─────────────────────────────────────
export function YouTubeEmbed({ videoId }) {
  if (!videoId) return null;
  return (
    <div className="youtube-embed">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title="YouTube video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

// ─── DOWNLOAD CARD ─────────────────────────────────────
export function DownloadCard({ icon, label, file, type }) {
  return (
    <a href={file} download className="download-card">
      <span className="download-icon">{icon}</span>
      <span className="download-label">{label}</span>
      <span className="download-type">{type || (file?.endsWith(".xlsx") ? "Excel" : "PDF")}</span>
      <span className="download-arrow">↓</span>
    </a>
  );
}

// ─── CHART BLOCK ───────────────────────────────────────
export function ChartBlock({ chartId }) {
  const chart = CHARTS[chartId];
  if (!chart) return null;

  return (
    <div className="chart-block">
      <div className="chart-title">{chart.title}</div>
      {chart.note && <div className="chart-note">{chart.note}</div>}
      {chart.type === "bar" && (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={chart.data} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--text-mid)" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "var(--text-mute)" }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 0, fontSize: 12 }}
              cursor={{ fill: "var(--card-hover)" }}
            />
            <Bar dataKey="points" fill="var(--ochre)" radius={[2,2,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
      {chart.type === "pie" && (
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={chart.data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
              {chart.data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
            </Pie>
            <Tooltip formatter={(v) => `$${v}`} contentStyle={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 0, fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── FADE UP HOOK ──────────────────────────────────────
export function useFadeUp() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) el.classList.add("visible"); }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}
